import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Subject, Observable, of } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { SeasonsService } from './seasons.service';
import { ISeason, ISeasonalReward } from './seasons.interfaces';
interface RewardRow {
  id: number;
  rank?: number;
  score?: number;
  quantity?: number;
  rewardId?: number;
  reward?: ISeasonalReward | null;
  createdAt?: string;
}

@Component({
  selector: 'app-season-rewards-dialog',
  templateUrl: './season-rewards-dialog.component.html',
  styles: [
    'table { width: 100%; } td, th { padding: 12px; text-align: left; } .error { color: #e85b5b; } img { height: 42px; max-width: 70px; object-fit: contain; }',
  ],
})
export class SeasonRewardsDialogComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private readonly refresh$ = new Subject<void>();
  rows: RewardRow[] = [];
  amount = 0;
  pageIndex = 0;
  pageSize = 20;
  loading = false;
  error = '';
  sort: Sort = { active: 'score', direction: 'asc' };
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: {
      userId: number;
      season: ISeason;
      mode: 'prizes' | 'score-rewards';
    },
    private readonly api: SeasonsService,
    public readonly ref: MatDialogRef<SeasonRewardsDialogComponent>,
  ) {}
  ngOnInit(): void {
    this.refresh$
      .pipe(
        switchMap(() => {
          this.loading = true;
          this.error = '';
          const params = {
            seasonId: this.data.season.id,
            pagination: {
              limit: this.pageSize,
              offset: this.pageIndex * this.pageSize,
            },
          };
          const request: Observable<{ rows: RewardRow[]; amount: number }> =
            this.data.mode === 'prizes'
              ? this.api
                  .getPrizes(this.data.userId, params)
                  .pipe(
                    map(page => ({
                      rows: page.userSeasonPrizes.map(row => ({
                        id: row.id,
                        rank: row.seasonPrize?.rank,
                        quantity: row.seasonPrize?.quantity,
                        rewardId: row.seasonPrize?.rewardId,
                        reward: row.seasonPrize?.reward,
                        createdAt: row.createdAt,
                      })),
                      amount: page.metadata.amount,
                    })),
                  )
              : this.api
                  .getScoreRewards(this.data.userId, {
                    ...params,
                    ...(this.sort.direction
                      ? {
                          sortBy:
                            (this.sort.direction === 'desc' ? '-' : '') +
                            'score',
                        }
                      : {}),
                  })
                  .pipe(
                    map(page => ({
                      rows: page.scoreRewards,
                      amount: page.metadata.amount,
                    })),
                  );
          return request.pipe(
            catchError(() => {
              this.error = 'Не удалось загрузить награды.';
              return of(null);
            }),
            finalize(() => (this.loading = false)),
          );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(page => {
        this.rows = page?.rows ?? [];
        this.amount = page?.amount ?? 0;
      });
    this.reload();
  }
  reload(): void {
    this.refresh$.next();
  }
  page(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.reload();
  }
  sortChanged(sort: Sort): void {
    this.sort = sort;
    this.pageIndex = 0;
    this.reload();
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
